import { createContext, useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, Outlet } from '@tanstack/react-router'
import type { SessionState } from '@/api'
import { keys, useApi, useDevices, useSession, useShares } from '@/api/hooks'
import { copy } from '@/copy'
import { Shell, PageHead } from '@/components/Shell'
import { SignIn } from '@/pages/SignIn'
import { Account } from '@/pages/Account'
import { useTitle } from '@/lib/useTitle'

type SignedIn = Extract<SessionState, { state: 'signed_in' }>

// The gate: until the session is known, a quiet page; a Purlview that cannot
// be reached is waited for, never taken for signed out (D-045); signed out is
// the sign-in; signed in is the console.
export function Root() {
  const session = useSession()
  if (session.data?.state === 'signed_in') return <Console session={session.data} />
  if (session.data) return <SignIn session={session.data} />
  if (session.isError) return <Unreachable retry={() => void session.refetch()} busy={session.isFetching} />
  return <div className="page" aria-busy="true" />
}

function Console({ session }: { session: SignedIn }) {
  const api = useApi()
  const client = useQueryClient()
  const shares = useShares()
  const devices = useDevices()
  const [signingOut, setSigningOut] = useState(false)
  const offline = (shares.isError && !!shares.data) || (devices.isError && !!devices.data)

  const signOut = async () => {
    if (signingOut) return
    setSigningOut(true)
    try {
      await api.signOut(session.csrf)
    } catch {
      // Signed out or not, this browser asks again and shows what it finds.
    }
    client.removeQueries({ queryKey: keys.shares })
    client.removeQueries({ queryKey: keys.devices })
    client.removeQueries({ queryKey: keys.browsers })
    await client.invalidateQueries({ queryKey: keys.session })
    setSigningOut(false)
  }

  return (
    <Shell
      account={session.account}
      counts={{ shares: shares.data?.shares.length, devices: devices.data?.devices.length }}
      offline={offline}
      onSignOut={signOut}
      signingOut={signingOut}
    >
      <AccountContext.Provider value={{ account: session.account, signOut, signingOut }}>
        <Outlet />
      </AccountContext.Provider>
    </Shell>
  )
}

const AccountContext = createContext<{ account: string; signOut: () => void; signingOut: boolean }>({ account: '', signOut: () => {}, signingOut: false })

export function AccountPage() {
  const { account, signOut, signingOut } = useContext(AccountContext)
  return <Account account={account} onSignOut={signOut} signingOut={signingOut} />
}

function Unreachable({ retry, busy }: { retry: () => void; busy: boolean }) {
  useTitle(copy.product)
  return (
    <div className="page flex items-center justify-center px-4">
      <div className="card w-full max-w-[440px] p-8" role="status">
        <p className="m-0 mb-5 text-[18px] font-medium">{copy.unreachable}</p>
        <button type="button" className="button w-full" onClick={retry} aria-disabled={busy || undefined}>
          {copy.tryAgain}
        </button>
      </div>
    </div>
  )
}

export function NotFound() {
  useTitle(copy.titles.notFound)
  return (
    <>
      <PageHead title={copy.notFound.heading} lead={copy.notFound.line} />
      <Link to="/" className="button">
        {copy.notFound.back}
      </Link>
    </>
  )
}
